import React, { useEffect, useMemo, useRef, useState } from 'react';
import './HabitCommitTracker.css';

const DAYS = Array.from({ length: 14 }, (_, index) => index + 1);

const STATUS_META = {
    tick: { symbol: '', className: 'tick' },
    cross: { symbol: '', className: 'cross' }
};

const HabitCommitTracker = ({ habits, trackerData = {}, onChangeCell, disabled = false }) => {
    const [menuState, setMenuState] = useState({ open: false, habitId: '', day: null, x: 0, y: 0 });
    const menuRef = useRef(null);

    const normalizedTracker = useMemo(() => {
        if (!trackerData || typeof trackerData !== 'object' || Array.isArray(trackerData)) {
            return {};
        }
        return trackerData;
    }, [trackerData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuState((prev) => ({ ...prev, open: false }));
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setMenuState((prev) => ({ ...prev, open: false }));
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const openMenu = (event, habitId, day) => {
        if (disabled) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        setMenuState({
            open: true,
            habitId,
            day,
            x: rect.left + rect.width / 2,
            y: rect.bottom + 8
        });
    };

    const handleSelectStatus = (status) => {
        if (!menuState.habitId || !menuState.day) {
            return;
        }

        onChangeCell(menuState.habitId, menuState.day, status);
        setMenuState((prev) => ({ ...prev, open: false }));
    };

    return (
        <div className="habit-commit-tracker">
            <div className="habit-commit-tracker__scroll">
                <table className="habit-commit-tracker__table">
                    <thead>
                        <tr>
                            <th className="habit-commit-tracker__habit-header">Habit</th>
                            {DAYS.map((day) => (
                                <th key={day} className="habit-commit-tracker__day-header">{day}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {habits.length === 0 && (
                            <tr>
                                <td className="habit-commit-tracker__empty">No habits selected</td>
                                {DAYS.map((day) => (
                                    <td key={`empty-${day}`} className="habit-commit-tracker__day-cell">&nbsp;</td>
                                ))}
                            </tr>
                        )}
                        {habits.map((habit) => (
                            <tr key={habit.id}>
                                <td className="habit-commit-tracker__habit-cell">{habit.label}</td>
                                {DAYS.map((day) => {
                                    const status = normalizedTracker[habit.id]?.[String(day)] || '';
                                    const statusMeta = STATUS_META[status];
                                    const statusClass = statusMeta ? `is-${statusMeta.className}` : '';

                                    return (
                                        <td key={`${habit.id}-${day}`} className="habit-commit-tracker__day-cell">
                                            <button
                                                type="button"
                                                className={`habit-commit-tracker__cell-button ${statusClass}`.trim()}
                                                onClick={(event) => openMenu(event, habit.id, day)}
                                                disabled={disabled}
                                                aria-label={`Set status for ${habit.label}, day ${day}`}
                                            >
                                                {statusMeta ? statusMeta.symbol : ''}
                                            </button>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {menuState.open && (
                <div
                    ref={menuRef}
                    className="habit-commit-tracker__menu"
                    style={{ left: `${menuState.x}px`, top: `${menuState.y}px` }}
                >
                    <button type="button" onClick={() => handleSelectStatus('tick')}>Tick ✓</button>
                    <button type="button" onClick={() => handleSelectStatus('cross')}>Cross ✕</button>
                    <button type="button" onClick={() => handleSelectStatus('')}>Clear</button>
                </div>
            )}
        </div>
    );
};

export default HabitCommitTracker;
